/**
 * The onboarding script's **schema, constants and validator** (FS-01 §4.1, §6.1).
 *
 * The script itself is not here. FS-01 R38 requires it be data rather than code so it is testable
 * and tunable without a rebuild, and FS-01 AC-20 asserts it is loaded as a JSON asset rather than
 * inlined in the bundle — so it lives at `public/content/onboarding.json` and is fetched at boot.
 * This module is the contract that file must satisfy.
 *
 * The constants below are **FS-01-local** (§6: "experience-level thresholds; they are not shared
 * constants"), which is why they are here and not in `src/core/constants.ts`. `MIN_NODE_WIDTH` is a
 * Design Bible number and is imported from there as usual — invariant 1 is untouched.
 */
import { MIN_NODE_WIDTH } from '../core/constants';
import { TEACH_ORDER } from './mechanics';
import type { HazardKind, MechanicId, PowerUpName, RevealHudId, TeachingDevice } from '../core/event-map';

export type { TeachingDevice, RevealHudId };

// ── FS-01 §6.1 tuning constants ─────────────────────────────────────────────

/** From page load to a controllable ship. Measured, not asserted — see RESONANCE.md. */
export const T_FIRST_INPUT = 6.0;
/** Non-lethal window. The first lethal hazard arrives at 16.27 s (bar 9.1). */
export const ONBOARD_PHANTOM_WINDOW = 16.0;
/**
 * A no-input player must survive at least this long. **FS-01 R44: this governs the onboarding
 * segment and nothing else.** Everywhere outside it, Design Bible §7.1's `MAX_SAFE_DWELL` (4.0 s)
 * applies and a stationary player must die. The two rules meet in this file and in
 * `onboarding-scheduler.ts`, nowhere else.
 */
export const T_AFK_SURVIVE = 18.0;
/** The skip control is not offered before this on a first run (R15). */
export const ONBOARD_SKIP_OFFER_T = 20.0;
/** Onboarding ends at bar 45.1, 89.49 s. */
export const ONBOARD_TOTAL = 90.0;
/** Enforces R07 with margin. */
export const ONBOARD_MIN_CLEARS_BEFORE_LETHAL = 3;

/** Sector 1 material — Sunset Boulevard, 118 BPM, 4/4 (Design Bible §9.3). */
export const ONBOARD_BPM = 118;
export const BEATS_PER_BAR = 4;

// ── §4.1 schema ─────────────────────────────────────────────────────────────

export const TEACHING_DEVICES: readonly TeachingDevice[] = [
  'beat-pulse',
  'node-highlight',
  'ghost-line',
  'funnel',
  'graze-flare',
  'control-glyph',
  'camera-lean',
  'stem-add',
  'haptic',
  'pickup-lure',
  'slot-pull',
  'polarity-mirror',
];

/**
 * Devices that fire when the hazard becomes visible rather than when it arrives. §5.2 describes
 * both kinds in one table — "its Node is lit brighter" happens on approach, "a pad stem enters"
 * happens on the pass — and the difference is what makes a teach a telegraph rather than a reward.
 */
export const TELEGRAPH_DEVICES: readonly TeachingDevice[] = [
  'beat-pulse',
  'node-highlight',
  'funnel',
  'control-glyph',
  'camera-lean',
  'ghost-line',
  'pickup-lure',
  'slot-pull',
  'polarity-mirror',
];

/**
 * The hazard a cue creates, if any. FS-01 §4.1 describes the script as "a normal sector Chart plus
 * an `onboarding` block", but this build has no baked chart format — the live scheduler generates
 * spawns — so the cue carries its own spawn. When charts are baked (FS-04 R49) this field is what
 * moves into the Chart half.
 */
export interface OnboardingSpawn {
  readonly kind: HazardKind;
  /** `rim` grows from the wall, `axis` from the centre. The polarity teach needs `axis`. */
  readonly polarity: 'rim' | 'axis';
  /** How far the material crosses the disc, 0..1. */
  readonly reach: number;
  /** A pickup riding this hazard's node, e.g. the scripted first Harmonic Shield at 40.7 s. */
  readonly pickup?: PowerUpName;
  /** Place the pickup only when Resonance is below this, for §5.2's bar-29.1 Overdrive guarantee. */
  readonly pickupIfResonanceBelow?: number;
}

export interface OnboardingCue {
  /** Musical position, "bar.beat", 1-indexed. Authoritative — the arrival beat, not the spawn. */
  readonly at: string;
  /** Derived, informative only; must equal the AudioClock time for `at` (AC-02). */
  readonly tSeconds: number;
  /** `null` for §5.2's "—" rows: density, not a new mechanic. */
  readonly mechanic: MechanicId | null;
  readonly devices: readonly TeachingDevice[];
  /** Non-lethal Phantom hazard (FS-01 R08). */
  readonly phantom: boolean;
  /** Lateral offset of the node centre from the tunnel axis, in u. */
  readonly nodeOffsetU: number;
  /** Node width in u. Must be >= MIN_NODE_WIDTH (Design Bible §7). */
  readonly nodeWidthU: number;
  readonly spawn?: OnboardingSpawn;
  /** HUD element to reveal on this cue, if any (FS-01 R13). */
  readonly revealHud?: RevealHudId;
}

export interface OnboardingScript {
  readonly sectorId: 'onboarding';
  readonly bpm: number;
  readonly phantomWindowSeconds: number;
  readonly firstLethalAt: string;
  readonly skipOfferAt: string;
  readonly cues: readonly OnboardingCue[];
}

// ── Musical position ────────────────────────────────────────────────────────

/** "bar.beat" (1-indexed) → seconds from the first downbeat, at `bpm`. */
export function beatPositionToSeconds(at: string, bpm = ONBOARD_BPM): number {
  const [barStr, beatStr] = at.split('.');
  const bar = Number(barStr);
  const beat = Number(beatStr);
  if (!Number.isFinite(bar) || !Number.isFinite(beat)) throw new Error(`bad bar.beat "${at}"`);
  return ((bar - 1) * BEATS_PER_BAR + (beat - 1)) * (60 / bpm);
}

// ── Validation (FS-01 AC-01 … AC-07) ────────────────────────────────────────

export class OnboardingScriptError extends Error {}

/**
 * Parse and check an unknown blob against the schema. Every rule here is an acceptance criterion,
 * so the loader and `tests/onboarding-script.test.ts` agree by construction: a script that fails in
 * the test cannot load in the game either.
 */
export function validateScript(raw: unknown): OnboardingScript {
  const s = raw as OnboardingScript;
  const fail = (msg: string): never => {
    throw new OnboardingScriptError(msg);
  };

  if (!s || typeof s !== 'object') fail('script is not an object');
  if (s.sectorId !== 'onboarding') fail(`sectorId is "${s.sectorId}", expected "onboarding"`);
  if (s.bpm !== ONBOARD_BPM) fail(`bpm is ${s.bpm}, expected ${ONBOARD_BPM} (Design Bible §9.3)`);
  if (!Array.isArray(s.cues) || s.cues.length === 0) fail('cues is empty');

  // AC-02 — strictly increasing, and tSeconds agrees with the clock within 1 ms.
  let prev = -1;
  for (const c of s.cues) {
    const t = beatPositionToSeconds(c.at, s.bpm);
    if (t <= prev) fail(`cue ${c.at} is not after the previous cue`);
    if (Math.abs(t - c.tSeconds) > 0.001) {
      fail(`cue ${c.at}: tSeconds ${c.tSeconds} != clock time ${t.toFixed(4)}`);
    }
    prev = t;

    // AC-04 — the node guarantee reaches the script too. A cue is content, and content may not
    // narrow the gap below the floor (Pillar P3).
    if (c.nodeWidthU < MIN_NODE_WIDTH) {
      fail(`cue ${c.at}: nodeWidthU ${c.nodeWidthU} < MIN_NODE_WIDTH ${MIN_NODE_WIDTH}`);
    }
    // AC-06 — no device outside the union, and none of them renders text.
    for (const d of c.devices) {
      if (!TEACHING_DEVICES.includes(d)) fail(`cue ${c.at}: unknown device "${d}"`);
    }
  }

  // AC-03 / R07 — at least three phantom cues that actually spawn something precede the first
  // lethal one. Counting cues rather than spawns would let three teach-moments with no hazard
  // satisfy "the player has cleared three hazards", which is not what R07 says.
  const firstLethalIndex = s.cues.findIndex((c) => !c.phantom);
  if (firstLethalIndex < 0) fail('no lethal cue: onboarding never becomes a game');
  const phantomSpawns = s.cues.slice(0, firstLethalIndex).filter((c) => c.spawn).length;
  if (phantomSpawns < ONBOARD_MIN_CLEARS_BEFORE_LETHAL) {
    fail(`only ${phantomSpawns} phantom hazards before the first lethal one (need ${ONBOARD_MIN_CLEARS_BEFORE_LETHAL})`);
  }

  // AC-05 — first-appearance order of mechanics equals the amended R11 order (FS-01 §5.3).
  const seen: MechanicId[] = [];
  for (const c of s.cues) {
    if (c.mechanic && !seen.includes(c.mechanic)) seen.push(c.mechanic);
  }
  const expected = TEACH_ORDER.join(' → ');
  if (seen.join(' → ') !== expected) fail(`teach order is "${seen.join(' → ')}", expected "${expected}"`);

  // AC-07 — the phantom window and the skip offer land where §6.1 says.
  const lethalT = beatPositionToSeconds(s.firstLethalAt, s.bpm);
  if (lethalT < ONBOARD_PHANTOM_WINDOW) {
    fail(`firstLethalAt ${s.firstLethalAt} (${lethalT.toFixed(2)}s) is before ONBOARD_PHANTOM_WINDOW`);
  }
  if (beatPositionToSeconds(s.skipOfferAt, s.bpm) < ONBOARD_SKIP_OFFER_T) {
    fail(`skipOfferAt ${s.skipOfferAt} is before ONBOARD_SKIP_OFFER_T`);
  }
  if (Math.abs(beatPositionToSeconds(s.cues[firstLethalIndex]!.at, s.bpm) - lethalT) > 0.001) {
    fail(`firstLethalAt ${s.firstLethalAt} does not match the first non-phantom cue`);
  }

  return s;
}
